import { type FC, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Alert,
  Checkbox,
  Icon,
  RadioButton as Radio,
  Select,
  Text,
  TextField
} from '@cencosud-cencommerce/paris-uikit';
import classNames from 'classnames';

import { useAppSelector } from '@/Application/Hooks/Redux';
import { isDniFeatureEnabled } from '@/config/featureFlags';
import { PHONE_PREFIX_OPTIONS, URL_ROUTES } from '@/constants';
import { redirectToUrl } from '@/modules/perseo-billing/utils/window';
import { IdentificationDocumentTypes } from '@/modules/shared/types/IdentificationDocumentType';
import { isWeddingCart } from '@/modules/shared/utils/isWeddingFlow';
import type { AddressGuestFormValues } from '@/sections/customerData/types/AddressGuestFormValues';

const CustomerDataForm: FC = () => {
  const customerForm = useFormContext<AddressGuestFormValues>();
  const cart = useAppSelector((state) => state?.cart?.data);

  const dniEnabled = isDniFeatureEnabled() && !isWeddingCart(cart);
  const {
    control,
    setValue,
    watch,
    resetField,
    formState: { errors }
  } = customerForm;

  const documentType = watch('documentType') || IdentificationDocumentTypes.RUT;
  const effectiveDocumentType = dniEnabled
    ? documentType
    : IdentificationDocumentTypes.RUT;

  useEffect(() => {
    if (!dniEnabled) {
      setValue('documentType', IdentificationDocumentTypes.RUT, {
        shouldValidate: false,
        shouldDirty: false
      });
    }
  }, [dniEnabled, setValue]);

  // reset phone when prefix changes
  const prefixPhone = watch('prefix-phone');
  useEffect(() => {
    resetField('phone', { defaultValue: '' });
  }, [prefixPhone, resetField]);

  return (
    <form
      id="customerDataForm"
      data-testid="customer-data-form"
      className="co-flex co-flex-col co-gap-6"
    >
      <div className="co-flex co-flex-col co-gap-6">
        <Text element="h2" variant="body1" weight={600}>
          Completa tus datos para procesar tu compra y la entrega de manera
          rápida y segura.
        </Text>
        <fieldset>
          <div>
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState, formState }) => (
                <TextField
                  {...field}
                  data-testid="customer-data-email"
                  label="Correo electrónico (*)"
                  iconName="email"
                  helperText={fieldState.error?.message}
                  status={
                    (formState.isSubmitted &&
                      (fieldState.invalid ? 'error' : 'validated')) ||
                    undefined
                  }
                />
              )}
            />
            {/* Teléfono */}
            <div
              className={classNames(
                'co-pt-[24px] co-flex co-flex-col co-gap-6 tablet_w:co-flex-row tablet_w:co-gap-2'
              )}
            >
              <div
                className={classNames(
                  'co-w-full co-min-w-[160px] tablet_w:co-w-1/3'
                )}
              >
                {/* phone prefix */}
                <Controller
                  control={control}
                  name="prefix-phone"
                  render={({ field, fieldState, formState }) => (
                    <Select
                      {...field}
                      data-testid="customer-data-prefix-phone"
                      options={PHONE_PREFIX_OPTIONS}
                      label="Código (*)"
                      disabled={!dniEnabled}
                      helperText={fieldState.error?.message}
                      status={
                        (formState.isSubmitted &&
                          (fieldState.invalid ? 'error' : 'validated')) ||
                        undefined
                      }
                    />
                  )}
                />
              </div>
              <div className="co-w-full tablet_w:co-w-2/3">
                {/* phone number */}
                <Controller
                  control={control}
                  name="phone"
                  render={({ field, fieldState, formState }) => (
                    <TextField
                      {...field}
                      data-testid="customer-data-phone"
                      label="Teléfono (*)"
                      type="tel"
                      helperText={fieldState.error?.message}
                      status={
                        (formState.isSubmitted &&
                          (fieldState.invalid ? 'error' : 'validated')) ||
                        undefined
                      }
                    />
                  )}
                />
              </div>
            </div>
            <div className={classNames('co-py-[24px]')}>
              <Alert
                body="Usaremos estos datos para verificar tu identidad y enviar información sobre tus compras."
                status="info"
              />
            </div>
          </div>
          {/* Name and Lastname */}
          <div>
            <Text variant="body1" weight={600}>
              ¿Cuál es tu nombre?
            </Text>
            <div className={classNames('co-pt-[24px] ')}>
              <Controller
                control={control}
                name="firstName"
                render={({ field, fieldState, formState }) => (
                  <TextField
                    {...field}
                    data-testid="customer-data-first-name"
                    label="Nombre (*)"
                    helperText={fieldState.error?.message}
                    status={
                      (formState.isSubmitted &&
                        (fieldState.invalid ? 'error' : 'validated')) ||
                      undefined
                    }
                  />
                )}
              />
            </div>
            <div className={classNames('co-pt-[24px] ')}>
              <Controller
                control={control}
                name="lastName"
                render={({ field, fieldState, formState }) => (
                  <TextField
                    {...field}
                    data-testid="customer-data-last-name"
                    label="Apellido (*)"
                    helperText={fieldState.error?.message}
                    status={
                      (formState.isSubmitted &&
                        (fieldState.invalid ? 'error' : 'validated')) ||
                      undefined
                    }
                  />
                )}
              />
            </div>
          </div>
          {/* Document Type Selector */}
          <div className={classNames('co-pt-[24px]')}>
            <Text variant="body1" weight={600}>
              ¿Cuál es tu número de documento?
            </Text>
            {dniEnabled && (
              <div className={classNames('co-pt-[24px] co-flex co-gap-4')}>
                <Controller
                  control={control}
                  name="documentType"
                  render={({ field: { value, onChange, ...rest } }) => {
                    const handleSelectRut = () => {
                      onChange(IdentificationDocumentTypes.RUT);
                      setValue('documentID', '', {
                        shouldValidate: false,
                        shouldDirty: false
                      });
                    };

                    const handleSelectDni = () => {
                      onChange(IdentificationDocumentTypes.DNI);
                      setValue('customerRut', '', {
                        shouldValidate: false,
                        shouldDirty: false
                      });
                    };

                    return (
                      <div className="co-flex co-flex-col co-gap-4">
                        <Radio
                          {...rest}
                          data-testid="customer-data-document-type-id"
                          label="RUT (Chile)"
                          checked={value === IdentificationDocumentTypes.RUT}
                          onChange={handleSelectRut}
                          variant="card"
                        />
                        <Radio
                          {...rest}
                          data-testid="customer-data-document-type-foreign"
                          label="DNI (otros países)"
                          checked={value === IdentificationDocumentTypes.DNI}
                          onChange={handleSelectDni}
                          variant="card"
                        />
                      </div>
                    );
                  }}
                />
              </div>
            )}
          </div>
          {/* RUT or Document Number - Conditional */}
          <div>
            {effectiveDocumentType === IdentificationDocumentTypes.RUT ? (
              <>
                <div className={classNames('co-pt-[24px] ')}>
                  <Controller
                    control={control}
                    name="customerRut"
                    render={({ field, fieldState, formState }) => (
                      <TextField
                        {...field}
                        data-testid="customer-data-rut"
                        label="RUT (*)"
                        type="rut"
                        helperText={fieldState.error?.message}
                        status={
                          (formState.isSubmitted &&
                            (fieldState.invalid ? 'error' : 'validated')) ||
                          undefined
                        }
                      />
                    )}
                  />
                </div>
                <div className={classNames('co-py-[24px] ')}>
                  <Alert
                    body="Verifica que el RUT sea el tuyo y que tus datos coincidan con el documento."
                    status="info"
                  />
                </div>
              </>
            ) : (
              <>
                <div className={classNames('co-pt-[24px] ')}>
                  <Controller
                    control={control}
                    name="documentID"
                    render={({
                      field: { onChange, ...field },
                      fieldState,
                      formState
                    }) => (
                      <TextField
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value.replace(
                            /[^A-Za-z0-9]/g,
                            ''
                          );
                          onChange(value);
                        }}
                        data-testid="customer-data-document-number"
                        label="Número de Documento (*)"
                        placeholder="Ej: AB123456"
                        maxLength={18}
                        helperText={fieldState.error?.message}
                        status={
                          (formState.isSubmitted &&
                            (fieldState.invalid ? 'error' : 'validated')) ||
                          undefined
                        }
                      />
                    )}
                  />
                </div>
                <div className={classNames('co-py-[24px] ')}>
                  <Alert
                    body="Verifica que el DNI sea el tuyo y que tus datos coincidan con el documento."
                    status="info"
                  />
                </div>
              </>
            )}
          </div>
          {/* Aceptar términos y condiciones */}
          <div className="co-flex">
            <Controller
              control={control}
              name="terms-and-cons"
              render={({ field: { value, onChange, ...rest } }) => (
                <Checkbox
                  {...rest}
                  data-testid="customer-data-terms-checkbox"
                  checked={value || false}
                  onChange={(e) => onChange(e.target.checked)}
                />
              )}
            />

            <div className="co-ml-[8px]">
              <Text variant="caption" weight={500}>
                Acepto los{' '}
                <Text
                  variant="link3"
                  weight={600}
                  onClick={() => redirectToUrl(URL_ROUTES.PARIS_TERMS_LINK)}
                >
                  Términos y Condiciones
                </Text>{' '}
                de Paris y la{' '}
                <Text
                  variant="link3"
                  weight={600}
                  onClick={() => redirectToUrl(URL_ROUTES.PRIVACY_POLICY_LINK)}
                >
                  Política de Privacidad de Datos
                </Text>
                .
              </Text>
            </div>
          </div>
          {/* error */}
          {errors['terms-and-cons']?.message && (
            <div className="co-flex co-items-center co-pt-[8px]">
              <Icon name="warning-circle-filled" fill="error-500" size={20} />{' '}
              <Text
                variant="caption"
                color="error-500"
                className={` co-pl-2 ${
                  errors['terms-and-cons'] ? 'co-text-error' : ''
                }`}
              >
                {errors['terms-and-cons'].message}
              </Text>
            </div>
          )}
        </fieldset>
      </div>
    </form>
  );
};

export default CustomerDataForm;
